<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../../Button.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import { EmployerProfileService, type EmployerProfileData } from '../../../lib/api/admin/employer-profile.service';
  import Notification from '../../Notification.svelte';
  import BackButton from '../../BackButton.svelte';
  import { push } from 'svelte-spa-router';
  import AddEmployerModal from './AddEmployerModal.svelte';
  import Breadcrumbs from '../../Breadcrumbs.svelte';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import ErrorState from '../../ErrorState.svelte';

  export let params: { id: string };
  
  let loading = false;
  let error: string | null = null;
  let profileData: EmployerProfileData | null = null;
  let employeesPage = 1;
  let itemsPerPage = 20;
  let employeesSearch = '';
  let searchTimeout: ReturnType<typeof setTimeout> | null = null;

  // Track previous ID to reload when it changes
  let previousId = '';
  $: if (params && params.id && params.id !== previousId) {
    previousId = params.id;
    loadEmployerProfile();
  }

  // Notification state
  let notificationShow = false;
  let notificationType: 'success' | 'error' = 'success';
  let notificationMessage = '';

  // Modal state
  let showEditModal = false;
  let employerToEdit: any = null;
  let showRejectModal = false;
  let showDeleteModal = false;
  let actionLoading = false;
  let rejectionReason = '';
  let outreachContactName = '';
  let outreachContactEmail = '';
  let outreachContactPhone = '';
  let sendingProfileLink = false;
  let showMergeModal = false;
  let mergeTargetUuid = '';
  let merging = false;

  // Table columns for employees
  const employeeColumns = [
    {
      key: 'full_name',
      label: 'Employee Name',
      width: '25%',
      render: (value: string) => `<span class="employee-name">${value}</span>`
    },
    {
      key: 'email',
      label: 'Email',
      width: '25%',
      render: (value: string) => `<a href="mailto:${value}" class="email-link">${value}</a>`
    },
    {
      key: 'job_title',
      label: 'Job Title',
      width: '20%',
      render: (value: string) => value || '—'
    },
    {
      key: 'department',
      label: 'Department',
      width: '15%',
      render: (value: string) => value || '—'
    },
    {
      key: 'location',
      label: 'Location',
      width: '15%',
      render: (value: string) => value || '—'
    }
  ];

  function showNotification(message: string, type: 'success' | 'error' = 'success') {
    notificationMessage = message;
    notificationType = type;
    notificationShow = true;
    setTimeout(() => notificationShow = false, 4000);
  }

  async function loadEmployerProfile() {
    if (!params || !params.id) {
      error = 'No employer ID provided';
      loading = false;
      return;
    }

    loading = true;
    error = null;
    
    try {
      const response = await EmployerProfileService.getEmployerProfile(
        params.id, 
        employeesPage, 
        itemsPerPage,
        employeesSearch
      );
      
      if (response.success && response.data) {
        profileData = response.data;
        outreachContactName = response.data.employer.contact_name || '';
        outreachContactEmail = response.data.employer.contact_email || '';
        outreachContactPhone = response.data.employer.contact_phone || '';
      } else {
        error = response.message || 'Failed to load employer profile';
        showNotification(error, 'error');
        profileData = null;
      }
    } catch (err: any) {
      error = err.message || 'Failed to load employer profile';
      showNotification(error ?? 'Failed to load employer profile', 'error');
      profileData = null;
    } finally {
      loading = false;
    }
  }

  function handleEmployeesPageChange(event: CustomEvent<{ page: number }>) {
    employeesPage = event.detail.page;
    loadEmployerProfile();
  }

  function handleSearchInput(event: CustomEvent<string>) {
    employeesSearch = event.detail;
    employeesPage = 1; // Reset to first page when searching
    
    // Debounce search (300ms)
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    searchTimeout = setTimeout(() => {
      loadEmployerProfile();
    }, 300);
  }

  function handleResetSearch() {
    employeesSearch = '';
    employeesPage = 1;
    loadEmployerProfile();
  }

  function handleRowClick(row: any) {
    push(`/admin/employee-profile/${row.uuid}`);
  }

  function formatCurrency(value: number): string {
    return EmployerProfileService.formatCurrency(value);
  }

  function renderStatus(status: string): string {
    const isApproved = status === 'approved';
    const isRejected = status === 'rejected';
    const isSeeded = status === 'seeded';
    const statusClass = isRejected ? 'status-rejected' : (isApproved ? 'status-approved' : (isSeeded ? 'status-seeded' : 'status-unapproved'));
    const statusText = isRejected ? 'Rejected' : (isApproved ? 'Approved' : (isSeeded ? 'Seeded' : 'Unapproved'));
    const statusIcon = isRejected
      ? '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>'
      : (isApproved 
        ? '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>'
        : '<svg class="status-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>');
    return `<span class="status-badge ${statusClass}">${statusIcon}${statusText}</span>`;
  }

  function handleEditDetails() {
    if (profileData?.employer) {
      employerToEdit = profileData.employer;
      showEditModal = true;
    }
  }

  function handleCloseModal() {
    showEditModal = false;
    employerToEdit = null;
  }

  async function handleSaveEmployer(data: any) {
    if (!params?.id) return;
    actionLoading = true;
    try {
      const result = await EmployerProfileService.updateEmployerProfile(params.id, {
        name: data.name,
        legal_name: data.legal_name,
        abn: data.abn,
        contact_name: data.contact_name,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,
        head_office_location: data.head_office_location,
      });
      if (result.success) {
        showNotification('Employer updated successfully', 'success');
        showEditModal = false;
        employerToEdit = null;
        await loadEmployerProfile();
      } else {
        showNotification(result.message || 'Failed to update employer', 'error');
      }
    } catch (err: any) {
      showNotification(err.message || 'Failed to update employer', 'error');
    } finally {
      actionLoading = false;
    }
  }

  async function handleSendProfileLink() {
    if (!params?.id || !outreachContactEmail.trim() || !outreachContactName.trim()) {
      showNotification('Contact name and email are required to send the profile link.', 'error');
      return;
    }
    sendingProfileLink = true;
    try {
      await EmployerProfileService.updateEmployerProfile(params.id, {
        contact_name: outreachContactName.trim(),
        contact_email: outreachContactEmail.trim(),
        contact_phone: outreachContactPhone.trim() || undefined,
      });
      const result = await EmployerProfileService.sendProfileCompletionLink(params.id, {
        contact_name: outreachContactName.trim(),
        contact_email: outreachContactEmail.trim(),
        contact_phone: outreachContactPhone.trim() || undefined,
        name: profileData?.employer.name,
        legal_name: profileData?.employer.legal_name || undefined,
      });
      if (result.success) {
        showNotification('Profile completion link sent.', 'success');
        await loadEmployerProfile();
      } else {
        showNotification(result.message || 'Failed to send profile link', 'error');
      }
    } catch (err: any) {
      showNotification(err.message || 'Failed to send profile link', 'error');
    } finally {
      sendingProfileLink = false;
    }
  }

  async function handleMergeEmployers() {
    if (!params?.id || !mergeTargetUuid.trim()) {
      showNotification('Enter the target employer UUID to merge into.', 'error');
      return;
    }
    merging = true;
    try {
      const result = await EmployerProfileService.mergeEmployers(params.id, mergeTargetUuid.trim());
      if (result.success) {
        showNotification('Employers merged successfully.', 'success');
        showMergeModal = false;
        push(`/employers/${mergeTargetUuid.trim()}`);
      } else {
        showNotification(result.message || 'Failed to merge employers', 'error');
      }
    } catch (err: any) {
      showNotification(err.message || 'Failed to merge employers', 'error');
    } finally {
      merging = false;
    }
  }

  $: canSendProfileLink = profileData?.employer?.status === 'seeded' || profileData?.employer?.status === 'unapproved';

  function openRejectModal() {
    showRejectModal = true;
  }

  function closeRejectModal() {
    if (!actionLoading) {
      showRejectModal = false;
      rejectionReason = '';
    }
  }

  function openDeleteModal() {
    showDeleteModal = true;
  }

  function closeDeleteModal() {
    if (!actionLoading) showDeleteModal = false;
  }

  async function handleConfirmReject() {
    if (!profileData?.employer?.uuid) return;
    actionLoading = true;
    try {
      const result = await EmployerProfileService.rejectEmployer(
        profileData.employer.uuid,
        rejectionReason.trim() || undefined
      );
      if (result.success) {
        showNotification('Employer rejected. You can now delete them if needed.', 'success');
        showRejectModal = false;
        rejectionReason = '';
        await loadEmployerProfile();
      } else {
        showNotification(result.message || 'Failed to reject employer', 'error');
      }
    } catch (err: any) {
      showNotification(err?.message || 'Failed to reject employer', 'error');
    } finally {
      actionLoading = false;
    }
  }

  async function handleConfirmDelete() {
    if (!profileData?.employer?.uuid) return;
    actionLoading = true;
    try {
      const result = await EmployerProfileService.deleteEmployer(profileData.employer.uuid);
      if (result.success) {
        showNotification('Employer deleted successfully', 'success');
        showDeleteModal = false;
        push('/employers');
      } else {
        showNotification(result.message || 'Failed to delete employer', 'error');
      }
    } catch (err: any) {
      showNotification(err?.message || 'Failed to delete employer', 'error');
    } finally {
      actionLoading = false;
    }
  }

  onMount(() => {
    if (params && params.id) {
      previousId = params.id;
      loadEmployerProfile();
    } else {
      error = 'No employer ID provided';
      loading = false;
    }
  });
</script>

<div class="employer-profile">
  <Breadcrumbs items={[
    { label: 'Admin', href: '/admin/dashboard' },
    { label: 'Employers', href: '/employers' },
    { label: profileData?.employer?.name || 'Employer Profile' }
  ]} />
  <BackButton text="Back" onClick={() => push('/employers')} />       
  {#if loading}
    <div style="min-height: 400px; padding: 2rem;">
      <LoadingSkeleton lines={8} height="2rem" />
    </div>
  {:else if error}
    <ErrorState 
      message="Failed to load employer profile" 
      description={error}
      onRetry={loadEmployerProfile}
    />
  {:else if profileData}
    <!-- Header Section with Statistics -->
    <div class="header-section">
      <div class="header-content">
        <h1>{profileData.employer.name}</h1>
        {#if profileData.employer.status}
          {@html renderStatus(profileData.employer.status)}
        {/if}
      </div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">Total Employees</div>
          <div class="stat-value">{profileData.statistics.total_employees.toLocaleString()}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Donations</div>
          <div class="stat-value">{formatCurrency(profileData.statistics.total_donations)}</div>
          {#if profileData.statistics.pending_donations > 0}
            <div class="stat-subtitle">Pending: {formatCurrency(profileData.statistics.pending_donations)}</div>
          {/if}
          {#if (profileData.statistics.awaiting_registration_donations ?? 0) > 0}
            <div class="stat-subtitle">Awaiting registration: {profileData.statistics.awaiting_registration_donations}</div>
          {/if}
        </div>
        <div class="stat-card">
          <div class="stat-label">Total Payouts</div>
          <div class="stat-value">{formatCurrency(profileData.statistics.total_payouts)}</div>
          {#if profileData.statistics.pending_payouts > 0}
            <div class="stat-subtitle">Pending: {formatCurrency(profileData.statistics.pending_payouts)}</div>
          {/if}
        </div>
      </div>
    </div>

    {#if profileData?.employer?.status === 'seeded'}
      <div class="details-card outreach-card">
        <h2>Employer outreach</h2>
        <p class="outreach-help">Employees can donate to this employer. Add a contact and send the profile completion link.</p>
        <div class="outreach-form">
          <div class="info-group">
            <label for="outreach-contact-name">Contact name</label>
            <input id="outreach-contact-name" type="text" bind:value={outreachContactName} class="outreach-input" />
          </div>
          <div class="info-group">
            <label for="outreach-contact-email">Contact email</label>
            <input id="outreach-contact-email" type="email" bind:value={outreachContactEmail} class="outreach-input" />
          </div>
          <div class="info-group">
            <label for="outreach-contact-phone">Contact phone</label>
            <input id="outreach-contact-phone" type="tel" bind:value={outreachContactPhone} class="outreach-input" />
          </div>
          <div class="outreach-actions">
            {#if canSendProfileLink}
              <Button variant="primary" on:click={handleSendProfileLink} disabled={sendingProfileLink}>
                {sendingProfileLink ? 'Sending…' : 'Send profile completion link'}
              </Button>
            {/if}
            <Button variant="secondary" on:click={() => showMergeModal = true}>Merge into another employer</Button>
          </div>
        </div>
      </div>
    {/if}

    <!-- Employer Details Card -->
    <div class="details-card">
      <div class="details-header">
        <h2>Employer Details</h2>
        <div class="details-actions">
          {#if profileData.employer.status === 'unapproved'}
            <Button
              variant="secondary"
              on:click={openRejectModal}
              class="reject-button"
            >
              Reject Employer
            </Button>
          {/if}
          <Button 
            variant="primary"
            on:click={handleEditDetails}
            class="edit-button"
          >
            Edit Details
          </Button>
          <Button
            variant="danger"
            on:click={openDeleteModal}
          >
            Delete Employer
          </Button>
        </div>
      </div>

      <div class="employer-info">
        <div class="info-group">
          <span class="info-label">Company Name</span>
          <div class="info-value">{profileData.employer.name}</div>
        </div>
        {#if profileData.employer.legal_name}
          <div class="info-group">
            <span class="info-label">Legal name</span>
            <div class="info-value">{profileData.employer.legal_name}</div>
          </div>
        {/if}
        {#if profileData.employer.industry?.name}
          <div class="info-group">
            <span class="info-label">Industry</span>
            <div class="info-value">{profileData.employer.industry.name}</div>
          </div>
        {/if}
        <div class="info-group">
          <span class="info-label">ABN</span>
          <div class="info-value">{profileData.employer.abn || '—'}</div>
        </div>
        <div class="info-group">
          <span class="info-label">Contact Name</span>
          <div class="info-value">{profileData.employer.contact_name || '—'}</div>
        </div>
        <div class="info-group">
          <span class="info-label">Contact Email</span>
          <div class="info-value">
            {#if profileData.employer.contact_email}
              <a href="mailto:{profileData.employer.contact_email}" class="info-link">{profileData.employer.contact_email}</a>
            {:else}
              —
            {/if}
          </div>
        </div>
        <div class="info-group">
          <span class="info-label">Contact Phone</span>
          <div class="info-value">{profileData.employer.contact_phone || '—'}</div>
        </div>
        <div class="info-group">
          <span class="info-label">Head Office Location</span>
          <div class="info-value">{profileData.employer.head_office_location || '—'}</div>
        </div>
        {#if profileData.employer.status === 'rejected' && profileData.employer.rejection_reason}
          <div class="info-group rejection-reason-block">
            <span class="info-label">Reason for rejection</span>
            <div class="info-value rejection-reason-text">{profileData.employer.rejection_reason}</div>
          </div>
        {/if}
      </div>
    </div>

    <!-- Employees Section -->
    <div class="employees-section">
      <div class="section-header">
        <h2>Employees</h2>
        {#if profileData.employees?.total > 0}
          <p class="section-subtitle">
            Showing {((employeesPage - 1) * itemsPerPage) + 1}-{Math.min(employeesPage * itemsPerPage, profileData.employees.total)} of {profileData.employees.total} employees
          </p>
        {/if}
      </div>

      <!-- Filters Section -->
      <div class="filters-section">
        <div class="filters-left">
          <div class="filter-item">
            <SearchInput
              placeholder="Search employees..."
              value={employeesSearch}
              on:input={handleSearchInput}
              width="250px"
            />
          </div>
          {#if employeesSearch}
            <div class="filter-item">
              <Button variant="secondary" on:click={handleResetSearch} class="reset-button">
                Reset
              </Button>
            </div>
          {/if}
        </div>
      </div>

      {#if loading}
        <div class="loading-container">
          <div class="loading-spinner"></div>
          <p>Loading employees...</p>
        </div>
      {:else if profileData.employees?.data?.length === 0}
        <div class="empty-state">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
          <h3>No employees found</h3>
          <p>This employer has no employees yet.</p>
        </div>
      {:else}
        <div class="table-container">
          <Table
            columns={employeeColumns}
            data={profileData.employees?.data || []}
            onRowClick={handleRowClick}
            loading={false}
          />
        </div>
        
        {#if profileData.employees && profileData.employees.total > itemsPerPage}
          <div class="pagination-container">
            <Pagination
              currentPage={employeesPage}
              totalPages={profileData.employees.last_page}
              on:pageChange={handleEmployeesPageChange}
            />
          </div>
        {/if}
      {/if}
    </div>
  {/if}
</div>

{#if showMergeModal}
  <div class="modal-overlay" role="dialog" aria-labelledby="merge-modal-title" aria-modal="true">
    <div class="modal-content" on:click|stopPropagation role="presentation">
      <h3 id="merge-modal-title">Merge employer</h3>
      <p>Move all donations and employees from <strong>{profileData?.employer?.name}</strong> into another employer record. This employer will be removed.</p>
      <div class="reject-reason-field">
        <label for="merge-target-uuid">Target employer UUID</label>
        <input id="merge-target-uuid" type="text" bind:value={mergeTargetUuid} class="outreach-input" placeholder="UUID of employer to keep" />
      </div>
      <div class="modal-actions">
        <Button variant="secondary" on:click={() => showMergeModal = false} disabled={merging}>Cancel</Button>
        <Button variant="primary" on:click={handleMergeEmployers} disabled={merging}>
          {merging ? 'Merging…' : 'Merge'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<!-- Edit Employer Modal -->
<AddEmployerModal
  open={showEditModal}
  employer={employerToEdit}
  onClose={handleCloseModal}
  onSave={handleSaveEmployer}
/>

<!-- Reject confirmation modal -->
{#if showRejectModal}
  <div class="modal-overlay" role="dialog" aria-labelledby="reject-modal-title" aria-modal="true">
    <div class="modal-content" on:click|stopPropagation role="presentation">
      <h3 id="reject-modal-title">Reject employer?</h3>
      <p>This employer will be marked as rejected and will no longer be able to use the system. You can then delete them from the list if needed.</p>
      <div class="reject-reason-field">
        <label for="rejection-reason">Reason for rejection (optional)</label>
        <textarea
          id="rejection-reason"
          bind:value={rejectionReason}
          placeholder="e.g. Employer declined to participate, duplicate entry…"
          rows="3"
          maxlength="1000"
          disabled={actionLoading}
          aria-describedby="rejection-reason-hint"
        />
        <span id="rejection-reason-hint" class="field-hint">If provided, this reason will be stored and sent to the user who nominated this employer.</span>
      </div>
      <div class="modal-actions">
        <Button variant="secondary" on:click={closeRejectModal} disabled={actionLoading}>Cancel</Button>
        <Button variant="primary" on:click={handleConfirmReject} disabled={actionLoading} class="reject-confirm-button">
          {actionLoading ? 'Rejecting…' : 'Reject'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<!-- Delete confirmation modal -->
{#if showDeleteModal}
  <div class="modal-overlay" role="presentation" on:click|self={closeDeleteModal} on:keydown={(e) => e.key === 'Escape' && closeDeleteModal()}>
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
      <h3 id="delete-modal-title">Delete Employer</h3>
      <p>Are you sure you want to delete <strong>{profileData?.employer?.name || 'this employer'}</strong>? This action cannot be undone and will remove all associated data.</p>
      <div class="modal-actions">
        <Button variant="secondary" on:click={closeDeleteModal} disabled={actionLoading}>Cancel</Button>
        <Button variant="danger" on:click={handleConfirmDelete} disabled={actionLoading}>
          {actionLoading ? 'Deleting...' : 'Delete'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<Notification 
  bind:show={notificationShow}
  type={notificationType}
  message={notificationMessage}
  duration={notificationType === 'error' ? 0 : 3000}
/>

<style>
  .employer-profile {
    padding: 1.5rem 2rem;
    width: 100%;
    background: #f7fafc;
  }

  .header-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .header-content h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1rem;
  }

  @media (max-width: 768px) {
    .stats-grid {
      grid-template-columns: 1fr;
    }
  }

  .stat-card {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .stat-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
  }

  .stat-subtitle {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }

  .loading-container,
  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .details-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
  }

  .details-header {
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .details-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .details-actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
  }

  :global(.reject-button) {
    border-color: #dc2626 !important;
    color: #dc2626 !important;
  }

  :global(.reject-button:hover) {
    background-color: #fef2f2 !important;
  }

  :global(.edit-button) {
    background-color: #f59e0b !important;
    color: white !important;
    border-radius: 9999px !important;
  }

  .employer-info {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;
  }

  .info-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .info-group .info-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .info-value {
    font-size: 1rem;
    color: #111827;
    padding: 0.75rem;
    background: #f9fafb;
    border-radius: 6px;
    word-break: break-word;
  }

  .info-link {
    color: var(--color-primary, #f59e0b);
    text-decoration: none;
    transition: color 0.2s;
  }

  .info-link:hover {
    color: var(--color-primary-hover, #f0a800);
    text-decoration: underline;
  }

  .employees-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;
  }

  .employees-section .filters-section {
    background: transparent;
    padding: 0;
    margin-bottom: 1.5rem;
    box-shadow: none;
    border: none;
  }

  .section-header {
    margin-bottom: 1rem;
  }

  /* Filters Section */
  .filters-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    flex: 1;
  }

  .filter-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .section-header h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
  }

  .section-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
  }

  .table-container {
    overflow-x: auto;
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    white-space: nowrap;
  }

  :global(.status-icon) {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  :global(.status-approved) {
    background-color: #dcfce7;
    color: #166534;
  }

  :global(.status-unapproved) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-rejected) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  .modal-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .modal-content {
    background-color: #fff;
    border-radius: 10px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    max-width: 450px;
    width: 90%;
    padding: 2rem;
  }

  .modal-content h3 {
    margin: 0 0 1rem 0;
    font-size: 1.25rem;
    color: #991b1b;
  }

  .modal-content p {
    color: #374151;
    margin: 0 0 1.5rem 0;
    line-height: 1.6;
  }

  .reject-reason-field {
    margin-bottom: 1.25rem;
  }

  .reject-reason-field label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.375rem;
  }

  .reject-reason-field textarea {
    width: 100%;
    min-height: 80px;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    font-family: inherit;
    resize: vertical;
    box-sizing: border-box;
  }

  .reject-reason-field textarea:focus {
    outline: none;
    border-color: var(--color-primary, #f59e0b);
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }

  .reject-reason-field textarea:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }

  .field-hint {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.375rem;
  }

  .rejection-reason-block {
    margin-top: 0.5rem;
  }

  .rejection-reason-text {
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    color: #991b1b;
    white-space: pre-wrap;
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
  }

  :global(.reject-confirm-button) {
    background-color: #dc2626 !important;
    color: white !important;
    border-color: #dc2626 !important;
  }

  :global(.reject-confirm-button:hover:not(:disabled)) {
    background-color: #b91c1c !important;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    color: #6b7280;
  }

  .empty-state svg {
    color: #9ca3af;
    margin-bottom: 1rem;
  }

  .empty-state h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .empty-state p {
    font-size: 0.875rem;
    margin: 0;
  }

  :global(.employee-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.email-link) {
    color: var(--color-primary, #f59e0b);
    text-decoration: none;
  }

  :global(.email-link:hover) {
    text-decoration: underline;
  }


  .outreach-card {
    margin-bottom: 1.5rem;
  }

  .outreach-help {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0 0 1rem;
  }

  .outreach-form {
    display: grid;
    gap: 1rem;
  }

  .outreach-input {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    box-sizing: border-box;
  }

  .outreach-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  :global(.status-seeded) {
    background-color: #fef3c7;
    color: #92400e;
  }

  /* Responsive Design */
  @media (max-width: 992px) {
    .employer-info {
      grid-template-columns: repeat(2, 1fr);
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 768px) {
    .employer-profile {
      padding: 1rem;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    .stats-grid {
      grid-template-columns: 1fr;
    }

    .employer-info {
      grid-template-columns: 1fr;
    }

    .filters-section {
      flex-direction: column;
      align-items: stretch;
    }

    .filters-left {
      width: 100%;
    }

    .filter-item {
      width: 100%;
    }
  }
</style>

